import { ExecutionResult, State, Workflow } from './types';
import moment from 'moment';
import 'moment-timezone';
import { ZERO_CONTACT_ARCHIVE_THRESHOLD } from '../../../functions/src/workflows/constants';

function nextRunDateTime(cronString: string) {
  const [minute, hour, dayOfMonth, month, dayOfWeek] = cronString.split(' ');
  const now = moment().tz('America/Los_Angeles');
  const nextRun = moment(now);

  // Helper function to check if a value matches the cron field
  function matches(value, cronField) {
    if (cronField === '*') return true;
    if (cronField.includes(','))
      return cronField.split(',').includes(value.toString());
    if (cronField.includes('-')) {
      const [start, end] = cronField.split('-');
      return value >= parseInt(start) && value <= parseInt(end);
    }
    if (cronField.includes('/')) {
      const [, step] = cronField.split('/');
      return value % parseInt(step) === 0;
    }
    return value.toString() === cronField;
  }

  // Find the next matching time
  while (true) {
    if (
      matches(nextRun.minutes(), minute) &&
      matches(nextRun.hours(), hour) &&
      matches(nextRun.date(), dayOfMonth) &&
      matches(nextRun.month() + 1, month) &&
      matches(nextRun.day(), dayOfWeek)
    ) {
      return nextRun.local();
    }
    nextRun.add(1, 'minute');
    nextRun.seconds(0);
    nextRun.milliseconds(0);
  }
}

/**
 * Traffic-light display status derived from raw RTDB fields.
 *
 * The raw scheduler `status` is 'paused' both when an operator pauses a
 * workflow and when the system pauses it after a fatal run error, so it cannot
 * be shown as-is. Precedence:
 * - Fatal last-run errors → ERRORED (needs admin intervention)
 * - Retryable last-run errors (rate limits, timeouts) stay off ERRORED so they
 *   can land in ACTIVE when still scheduled, or PAUSED if manually paused
 * - PAUSED is reserved for workflows stopped without a fatal error
 */
export enum WorkflowDisplayStatus {
  ACTIVE = 'active', // green
  PAUSED = 'paused', // amber
  ERRORED = 'errored', // red
  ARCHIVED = 'archived',
}

type WorkflowDisplayFields = {
  isArchived?: boolean;
  status?: string;
  last_execution_result?: string;
  error?: string;
};

/**
 * Retryable backend errors (rate limits, timeouts) — operator can still queue a run.
 * Matches uiError copy that ends with "will retry automatically".
 */
export function isWorkflowRetryableError(error?: string): boolean {
  if (!error?.trim()) {
    return false;
  }

  return error.toLowerCase().includes('will retry automatically');
}

/** Search box scope — This tab keeps tab filters; All statuses searches every status. */
export type WorkflowSearchScope = 'tab' | 'all';

/** Default cross-tab search result order: active → paused → errored → archived. */
export const WORKFLOW_DISPLAY_STATUS_SORT_ORDER: Record<
  WorkflowDisplayStatus,
  number
> = {
  [WorkflowDisplayStatus.ACTIVE]: 0,
  [WorkflowDisplayStatus.PAUSED]: 1,
  [WorkflowDisplayStatus.ERRORED]: 2,
  [WorkflowDisplayStatus.ARCHIVED]: 3,
};

export function compareWorkflowsByDisplayStatus(
  a: WorkflowDisplayFields,
  b: WorkflowDisplayFields
): number {
  return (
    WORKFLOW_DISPLAY_STATUS_SORT_ORDER[getWorkflowDisplayStatus(a)] -
    WORKFLOW_DISPLAY_STATUS_SORT_ORDER[getWorkflowDisplayStatus(b)]
  );
}

export function getWorkflowDisplayStatus(
  workflow: WorkflowDisplayFields
): WorkflowDisplayStatus {
  if (workflow.isArchived === true) {
    return WorkflowDisplayStatus.ARCHIVED;
  }
  // Fatal errors only — retryable failures (rate limit, timeout) stay Active
  // when still scheduled so Errored means "needs intervention".
  if (
    workflow.last_execution_result === 'error' &&
    !isWorkflowRetryableError(workflow.error)
  ) {
    return WorkflowDisplayStatus.ERRORED;
  }
  if (workflow.status === 'paused') {
    return WorkflowDisplayStatus.PAUSED;
  }
  return WorkflowDisplayStatus.ACTIVE;
}

export type WorkflowTrafficLightTone = 'green' | 'amber' | 'red' | 'neutral';

/**
 * Tone for the Cloud Scheduler status badge.
 * Manual pause stays amber; system-pause after a fatal error is red so yellow
 * remains "wait / retry" and red remains "take action". Other tabs are unchanged.
 */
export function getSchedulerStatusBadgeTone(
  workflow: WorkflowDisplayFields
): WorkflowTrafficLightTone {
  if (workflow.status === State.PAUSED) {
    return getWorkflowDisplayStatus(workflow) === WorkflowDisplayStatus.ERRORED
      ? 'red'
      : 'amber';
  }
  if (workflow.status === State.ENABLED) {
    return 'green';
  }
  return 'neutral';
}

/** Human-readable status label — spells out *why* a workflow is stopped. */
export function getWorkflowStatusLabel(workflow: WorkflowDisplayFields): string {
  switch (getWorkflowDisplayStatus(workflow)) {
    case WorkflowDisplayStatus.ARCHIVED:
      return 'Archived';
    case WorkflowDisplayStatus.ERRORED:
      if (workflow.status === 'paused') {
        return 'Errored — paused by system';
      }
      if (workflow.status === 'enabled') {
        return 'Errored — still scheduled';
      }
      return `Errored — scheduler ${workflow.status}`;
    case WorkflowDisplayStatus.PAUSED:
      return 'Paused — manually paused';
    default:
      if (
        workflow.last_execution_result === 'error' &&
        isWorkflowRetryableError(workflow.error)
      ) {
        return workflow.status === 'enabled'
          ? 'Active — will retry'
          : `Scheduler ${workflow.status}`;
      }
      return workflow.status === 'enabled'
        ? 'Active'
        : `Scheduler ${workflow.status}`;
  }
}

/**
 * Short Error-column labels for the workflows table.
 * Grouped generics — full detail and action guidance stay in the modal.
 */
export function getWorkflowErrorShortLabel(error?: string): string {
  if (!error?.trim()) {
    return '';
  }

  if (isWorkflowRetryableError(error)) {
    return 'Temporary issue — retrying';
  }

  const e = error.toLowerCase();

  if (
    e.includes('has been decommissioned') ||
    (e.includes('is inactive') &&
      (e.includes('source') || e.includes('destination'))) ||
    (e.includes('not found') &&
      (e.includes('source') || e.includes('destination')))
  ) {
    return 'Account issue';
  }
  if (
    e.includes('is not published') ||
    (e.includes('was not found') &&
      (e.includes('webform') || e.includes('form')))
  ) {
    return 'Form issue';
  }
  if (e.includes('ifb validation failed')) {
    return 'Validation issue';
  }
  if (
    e.includes('form-builder credential') ||
    e.includes('form-builder api credentials') ||
    e.includes('authentication failed') ||
    e.includes('vault credentials') ||
    e.includes('unknown vault') ||
    e.includes('unable to load form-builder')
  ) {
    return 'Credentials issue';
  }
  if (e.includes('addressbook')) {
    return 'Addressbook issue';
  }
  if (
    e.includes('could not be migrated') ||
    e.includes('something went wrong')
  ) {
    return 'Migration incomplete';
  }

  return 'Error';
}

/**
 * Operator-facing next step for the workflow modal.
 */
export function getWorkflowErrorActionGuidance(error?: string): string {
  if (!error?.trim()) {
    return 'Review the error details, resolve the issue, then resume the workflow.';
  }

  if (isWorkflowRetryableError(error)) {
    return 'No action needed — the workflow will retry automatically.';
  }

  const e = error.toLowerCase();

  if (e.includes('has been decommissioned')) {
    return 'This workflow was archived because the account was decommissioned and cannot be resumed.';
  }
  if (e.includes('is not published')) {
    return 'Publish the form in IFB, then resume the workflow.';
  }
  if (
    e.includes('was not found') &&
    (e.includes('webform') || e.includes('form'))
  ) {
    return 'Check the workflow form ID in IFB, then resume the workflow.';
  }
  if (e.includes('ifb validation failed')) {
    return 'Fix the webform configuration or contact profile fields, then resume the workflow.';
  }
  if (
    e.includes('vault credentials') ||
    e.includes('unknown vault') ||
    e.includes('unable to load form-builder') ||
    e.includes('form-builder credential') ||
    e.includes('form-builder api credentials') ||
    e.includes('authentication failed')
  ) {
    return 'Check the form-builder credential for this account in DAM — it should be present and active — then resume the workflow.';
  }
  if (e.includes('addressbook')) {
    return 'Fix the source/destination addressbook (exists, not deleted from DD, destination consented), then resume the workflow.';
  }
  if (
    e.includes('is inactive') &&
    (e.includes('source') || e.includes('destination'))
  ) {
    return 'Reactivate the inactive account (or update the workflow accounts), then resume the workflow.';
  }
  if (
    e.includes('not found') &&
    (e.includes('source') || e.includes('destination'))
  ) {
    return 'Correct the missing source/destination account on the workflow, then resume.';
  }
  if (e.includes('could not be migrated')) {
    return 'Some contacts failed to migrate — check source/destination counts and logs before the next run.';
  }
  if (e.includes('something went wrong')) {
    return 'Unexpected failure — check logs, then resume or re-run the workflow.';
  }

  return 'Resolve the issue above, then resume the workflow.';
}

/**
 * Modal detail line — when the error is form-related, include the IFB form title.
 */
export function formatWorkflowModalError(
  error?: string,
  formTitle?: string | null,
  formId?: string | null
): string {
  const detail = error?.trim() || 'Unknown Error';
  if (!formTitle) {
    return detail;
  }

  const isFormError =
    /ifb webform|ifb validation|is not published|was not found/i.test(detail);
  if (!isFormError) {
    return detail;
  }

  if (formId && detail.includes(formId)) {
    return detail.replace(formId, `${formTitle} (${formId})`);
  }

  return `${detail} Form: ${formTitle}.`;
}

/**
 * Modal "Run now" — only for manually paused workflows without a blocking fatal error.
 * Enabled/paused-by-system (IFB cred, unpublished form, etc.) are excluded.
 * Same callable as the table action; neither path uses a confirmation dialog.
 */
export function canRunWorkflowNowInModal(workflow: {
  isArchived?: boolean;
  status?: string;
  last_execution_result?: string;
  error?: string;
}): boolean {
  if (!workflow || workflow.isArchived === true) {
    return false;
  }
  if (workflow.status !== State.PAUSED) {
    return false;
  }
  if (workflow.last_execution_result === ExecutionResult.RUNNING) {
    return false;
  }
  if (workflow.last_execution_result === ExecutionResult.ERROR) {
    return isWorkflowRetryableError(workflow.error);
  }

  return true;
}

/** Human-readable Daily Schedule column — reflects run state, not legacy lifecycle stages. */
export function getReadableWorkflowLifecycleStage(workflow: Workflow) {
  const nextRun = nextRunDateTime(workflow.schedule || '0 0 * * *');
  const streak = workflow.zero_contact_streak ?? 0;

  // Mid-run: RTDB shows running; don't imply the workflow is idle until tomorrow
  if (workflow.last_execution_result === 'running') {
    return 'daily; migration in progress';
  }

  if (streak > 0) {
    // 30 consecutive zero-contact days triggers backend auto-archive (see archiveEligibility)
    return `daily; no contacts (${streak}/${ZERO_CONTACT_ARCHIVE_THRESHOLD} days); next run: ${nextRun.format('ddd, DD')}`;
  }

  // Last run failed but scheduler still enabled — next cron fire is scheduled
  if (
    workflow.last_execution_result === 'error' &&
    workflow.status === 'enabled'
  ) {
    const retryHint = isWorkflowRetryableError(workflow.error)
      ? ' (will retry)'
      : '';
    return `daily; last run failed${retryHint}; next run: ${nextRun.format('ddd, DD')}`;
  }

  // Default daily schedule copy; paused/disabled workflows show scheduler status instead of next run
  let subtitle = `next run: ${nextRun.format('ddd, DD')}`;
  if (workflow.status !== 'enabled') subtitle = `workflow ${workflow.status}`;
  return `daily; ${subtitle}`;
}

export type WorkflowSearchRow = {
  name?: string;
  key?: string;
  type?: string;
  subtype?: string;
  error?: string;
  description?: string;
  isArchived?: boolean;
  status?: string;
  last_execution_result?: string;
  args?: {
    source_account?: number;
    source_addressbook?: number;
    destination_account?: number;
    destination_addressbook?: number;
    form_id?: string;
  };
};

/** Flattened search blob for global search across all workflow tabs. */
export function getWorkflowGlobalSearchValue(row: WorkflowSearchRow): string {
  return [
    row.key,
    row.name,
    row.type,
    row.subtype,
    row.error,
    getWorkflowErrorShortLabel(row.error),
    getWorkflowStatusLabel(row),
    getDescriptionSearchValue(row),
  ]
    .filter(Boolean)
    .join('\n');
}

/** Search blob for Description column — description text plus bare IDs from args. */
export function getDescriptionSearchValue(row: WorkflowSearchRow): string {
  const parts = [row.description || ''];
  const { args } = row;

  if (args) {
    if (args.source_account != null) {
      parts.push(String(args.source_account));
    }
    if (args.source_addressbook != null) {
      parts.push(String(args.source_addressbook));
    }
    if (args.destination_account != null) {
      parts.push(String(args.destination_account));
    }
    if (args.destination_addressbook != null) {
      parts.push(String(args.destination_addressbook));
    }
    if (args.form_id) {
      parts.push(args.form_id);
    }
  }

  return parts.join('\n');
}

const normalizeDescriptionQuery = (filterValue: string) =>
  String(filterValue).trim().toLowerCase().replace(/[()]/g, '');

/** Match pasted account/addressbook IDs — with or without parentheses. */
export function workflowMatchesDescriptionFilter(
  row: WorkflowSearchRow,
  filterValue: string
): boolean {
  const query = normalizeDescriptionQuery(filterValue);
  if (!query) {
    return true;
  }

  const searchBlob = getDescriptionSearchValue(row).toLowerCase();
  const blobWithoutParens = searchBlob.replace(/[()]/g, '');

  if (
    searchBlob.includes(query) ||
    blobWithoutParens.includes(query)
  ) {
    return true;
  }

  const args = row.args;
  if (!args) {
    return false;
  }

  const ids = [
    args.source_account,
    args.source_addressbook,
    args.destination_account,
    args.destination_addressbook,
    args.form_id,
  ]
    .filter((value) => value != null && `${value}` !== '')
    .map((value) => String(value).toLowerCase());

  if (/^\d+$/.test(query)) {
    return ids.some((id) => id === query);
  }

  return ids.some((id) => id.includes(query));
}

export const descriptionColumnFilterFn = (rows, _id, filterValue) => {
  if (!filterValue) {
    return rows;
  }

  return rows.filter((row) =>
    workflowMatchesDescriptionFilter(row.original, filterValue)
  );
};

descriptionColumnFilterFn.autoRemove = (val) => !val;
